import { I18n } from '@calendly/i18n';
import { Segment } from '@calendly/segment';
import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { Icon } from '@calendly/ui/components/icon';
import { AlertWarningCircleIcon, MegaphoneIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { TextInput } from '@calendly/ui/components/text-input';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { flatten, isEqual } from 'lodash-es';
import { useEffect, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { Layout } from 'components/layout';
import { useTrackAvailabilityInteraction } from 'hooks/analytics_events/use_track_availability_interaction';
import { currentOrganizationSelector } from 'selectors/current_organization';
import { EventTypeList } from './event_type_list';
import { useGetAvailabilityEventTypesQuery, useUpdateAvailabilityLinkedEventTypesMutation } from '../../../api';
import { useLinkedAvailabilityEventTypes, useNonLinkedEventTypes } from '../../../hooks';
import { filterEventTypesByText } from '../../../utils';
import { StyledDropdownMenu } from '../styles';
const containerCss = "cn8rq6j";
const Container = forwardRef((props, ref) => {
  const align = 'center';
  const gap = '4';
  return <Layout ref={ref} {...props} align={align} gap={gap} className={classNames(containerCss, props.className)} />;
});
const dropdownWrapperCss = "d388b0c";
const DropdownWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(dropdownWrapperCss, props.className)} />;
});
const styledDropdownToggleVariantBannerCss = "sxf4r85";
const styledDropdownToggleCss = "s1eq3cne";
const StyledDropdownToggle = forwardRef((props, ref) => {
  const {
    variant,
    ...restProps
  } = props;
  return <BareButton ref={ref} {...restProps} className={classNames(styledDropdownToggleCss, {
    [styledDropdownToggleVariantBannerCss]: variant?.toString() === 'banner'
  }, props.className)} />;
});
const styledTextInputCss = "sonr0ww";
const StyledTextInput = forwardRef((props, ref) => <TextInput ref={ref} {...props} className={classNames(styledTextInputCss, props.className)} />);
const ListControls = props => <Layout align="center" paddingX="4" paddingY="2" {...props} />;
const eventTypeListContainerCss = "e1z9d3x";
const EventTypeListContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(eventTypeListContainerCss, props.className)} />;
});
const footerContainerIsPhoneCss = "fvq6jz5";
const footerContainerCss = "f1y1wfqx";
const FooterContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(footerContainerCss, {
    [footerContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledButtonCss = "s1r671lk";
const StyledButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
const InlineContainer = ({
  children
}) => <Container>
    <BodyText size="medium" weight="semibold">
      {I18n.t('publisher.availability.schedules.active_event_types_dropdown.title')}
      :
    </BodyText>
    {children}
  </Container>;
const BannerContainer = ({
  children
}) => <ContextualBanner icon={MegaphoneIcon} variant="standard">
    <BodyText size="medium">
      {I18n.t('publisher.availability_page.default_schedule.header.event_types_update')}
      {' '}
      {children}
    </BodyText>
  </ContextualBanner>;
const containers = {
  inline: InlineContainer,
  banner: BannerContainer
};
export const ActiveOn = ({
  selectedScheduleId,
  currentEventType,
  trackLocation,
  variant = 'inline'
}) => {
  const ContainerComponent = containers[variant];
  const {
    data,
    isFetching,
    isError
  } = useGetAvailabilityEventTypesQuery();
  const currentOrganization = useSelector(currentOrganizationSelector);
  const ownerName = currentOrganization?.owner.name;
  const managerCount = currentOrganization.managing_members_count;
  const initNonLinkedEventTypes = useNonLinkedEventTypes(data, selectedScheduleId, ownerName, managerCount, currentEventType);
  const initLinkedEventTypes = useLinkedAvailabilityEventTypes(data, selectedScheduleId, ownerName, managerCount, currentEventType);
  const linkedEventTypesCount = initLinkedEventTypes.reduce((sum, profile) => sum + profile.eventTypes.length, 0);
  const initLinkedEventTypesCount = currentEventType ? linkedEventTypesCount + 1 : linkedEventTypesCount;
  const noLinkedEventTypes = data && !isFetching && !isError && !initLinkedEventTypesCount;
  const isPhone = useDeviceType('isPhone');
  const [setLinkedEventTypes, {
    isLoading
  }] = useUpdateAvailabilityLinkedEventTypesMutation();
  const [toggleDropdown, Dropdown, dropdownIsOpen] = useDropdown();
  const [search, setSearch] = useState('');
  const initLinkedEventTypeIds = flatten(initLinkedEventTypes.map(({
    eventTypes
  }) => eventTypes.map(({
    id
  }) => id)));
  const initNonLinkedEventTypeIds = flatten(initNonLinkedEventTypes.map(({
    eventTypes
  }) => eventTypes.map(({
    id
  }) => id)));
  const [linked, setLinked] = useState(initLinkedEventTypeIds);
  const filteredInitLinkedEventTypes = filterEventTypesByText(initLinkedEventTypes, search);
  const filteredInitNonLinkedEventTypes = filterEventTypesByText(initNonLinkedEventTypes, search);
  useEffect(() => {
    if (!dropdownIsOpen) {
      setSearch('');
      if (!isEqual(linked.sort(), initLinkedEventTypeIds.sort())) setLinked(initLinkedEventTypeIds);
    }
  }, [dropdownIsOpen, linked, initLinkedEventTypeIds, setLinked]);
  const onChange = (eventTypeId, enabled) => {
    if (enabled) {
      setLinked(linked.concat([eventTypeId]));
    } else {
      setLinked(linked.filter(id => String(id) !== String(eventTypeId)));
    }
  };
  const onSelectAll = () => {
    const accessibleNonLinkedIds = initNonLinkedEventTypes.map(profile => profile.eventTypes.map(eventType => eventType.canEdit ? eventType.id : null).filter(el => el !== null));
    setLinked(flatten(accessibleNonLinkedIds).concat(initLinkedEventTypeIds));
  };
  const onDeselectAll = () => {
    const inaccessibleLinkedIds = initLinkedEventTypes.map(profile => profile.eventTypes.map(eventType => eventType.canEdit ? null : eventType.id).filter(el => el !== null));
    setLinked(flatten(inaccessibleLinkedIds));
  };
  const trackActiveOnOpen = useTrackAvailabilityInteraction({
    location: trackLocation,
    action: 'view_schedule_active_event_types'
  });
  const trackActiveOnApply = useTrackAvailabilityInteraction({
    location: trackLocation,
    action: 'apply_schedule'
  });
  const onSave = async () => {
    if (!isEqual(linked.sort(), initLinkedEventTypeIds.sort())) {
      initLinkedEventTypes.concat(initNonLinkedEventTypes).map(({
        eventTypes
      }) => eventTypes).flat().filter(et => initNonLinkedEventTypeIds.includes(et.id) === linked.includes(et.id)).forEach(({
        id,
        kind
      }) => {
        Segment.track('event_type.availability.updated', {
          event_type_kind: kind,
          availability_rule: linked.includes(id) ? 'schedule' : 'custom',
          availability_rule_change_method: 'global'
        });
      });
      trackActiveOnApply({
        updated_event_type_id: linked
      });
      await setLinkedEventTypes({
        id: selectedScheduleId,
        linked
      });
    }
    toggleDropdown();
  };
  if (isError) return null;
  if (isFetching) {
    return <ContainerComponent>
        <Loader size="small" inline />
      </ContainerComponent>;
  }
  const handleClick = () => {
    if (!dropdownIsOpen) trackActiveOnOpen();
    toggleDropdown();
  };
  return <>
      <ContainerComponent>
        {noLinkedEventTypes && <AlertWarningCircleIcon spacingLeft="4" />}
        <DropdownWrapper>
          <StyledDropdownToggle onClick={handleClick} variant={variant}>
            <BodyText size="medium" weight="semibold">
              {I18n.t('publisher.availability_page.default_schedule.header.event_type_count', {
              count: initLinkedEventTypesCount
            })}
            </BodyText>
            <Icon name={dropdownIsOpen ? 'angle-up' : 'angle-down'} />
          </StyledDropdownToggle>
          <Dropdown>
            <StyledDropdownMenu data-component="active-on-dropdown">
              <StyledTextInput autoFocus placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
              <ListControls>
                <StyledButton onClick={onSelectAll}>
                  {I18n.t('publisher.availability_page.default_schedule.header.active_event_types_dropdown.select_all')}
                </StyledButton>
                /
                <StyledButton onClick={onDeselectAll}>
                  {I18n.t('publisher.availability_page.default_schedule.header.active_event_types_dropdown.deselect_all')}
                </StyledButton>
              </ListControls>
              <EventTypeListContainer>
                <EventTypeList selectedScheduleId={selectedScheduleId} linkedEventTypes={filteredInitLinkedEventTypes} nonLinkedEventTypes={filteredInitNonLinkedEventTypes} linkedEventTypeIds={linked} onChange={onChange} currentEventType={currentEventType} />
              </EventTypeListContainer>
              <FooterContainer>
                <Button size={isPhone ? 'normal' : 'small'} onClick={onSave} loading={isLoading}>
                  {I18n.t('publisher.action.apply')}
                </Button>
                {!isPhone && <Button size="small" decoration="ghost" onClick={toggleDropdown}>
                    {I18n.t('publisher.action.cancel')}
                  </Button>}
              </FooterContainer>
            </StyledDropdownMenu>
          </Dropdown>
        </DropdownWrapper>
      </ContainerComponent>
      {noLinkedEventTypes && <ContextualBanner icon={AlertWarningCircleIcon} variant="warning">
          <BodyText>
            {I18n.t('publisher.availability_page.default_schedule.header.no_linked_event_types_warning')}
          </BodyText>
        </ContextualBanner>}
    </>;
};

require("./active_on.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./active_on.js");